import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  SvgIcon
} from '@mui/material';
import { Close as CloseIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { DATA_OG, DATA_TEST } from '../../enums';
import { IEntityDetails, IProductDetails, OnEntitySelectCallback } from '../../types';
import { navigateToEntityUrl } from '../EntitySwitcher/onEntitySelectUtils';

import { AppSwitcherSelectionCard } from './AppSwitcherSelectionCard';

interface IAppSwitcherSelectionDialogProps {
  open: boolean;
  selectedEntity: IEntityDetails;
  fullScreen?: boolean;
  onEntitySelect?: OnEntitySelectCallback;
  handleClose(): void;
}
export const AppSwitcherSelectionDialog: React.FC<IAppSwitcherSelectionDialogProps> = ({
  open,
  selectedEntity,
  fullScreen = false,
  onEntitySelect,
  handleClose
}) => {
  const [selectedApp, setSelectedApp] = React.useState<IProductDetails>(null);
  const onClose = () => {
    handleClose();
    setTimeout(() => setSelectedApp(null), 500);
  };
  // FullScreen indicates it is a mobile/tablet/condensed view.
  // Occupy the entire screen width for mobile view,
  // but adjust the grid column count based on the
  // number of applications for desktop view.
  let gridColumnCount;
  if (fullScreen) {
    gridColumnCount = 1;
  } else {
    const applicationsLength = selectedEntity?.entitledApplications?.length;
    if (applicationsLength && applicationsLength > 2) {
      gridColumnCount = 3;
    } else {
      gridColumnCount = 2;
    }
  }

  return (
    <Dialog
      fullScreen={fullScreen}
      onClose={onClose}
      open={open}
      maxWidth={false}
      data-test={DATA_TEST.app_switcher_selection_dialog_container}
    >
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <DialogTitle variant="h2">Select Application</DialogTitle>
        <IconButton
          sx={{ height: '40px', width: '40px', marginRight: '20px' }}
          aria-label="close"
          onClick={handleClose}
          data-test={DATA_TEST.app_switcher_close_icon}
          data-og={DATA_OG.app_switcher_close_icon}
        >
          <SvgIcon inheritViewBox component={CloseIcon} />
        </IconButton>
      </Box>

      <DialogContent>
        <DialogContentText fontWeight={500}>
          {`Select an application to continue to ${selectedEntity?.displayName || selectedEntity?.name}`}
        </DialogContentText>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${gridColumnCount}, 1fr)`,
            gap: 2
          }}
        >
          {selectedEntity?.entitledApplications?.map((appDetails) => {
            return (
              <AppSwitcherSelectionCard
                fullScreen={fullScreen}
                key={appDetails.displayName}
                appDetails={appDetails}
                selected={appDetails.entitlementProductKey === selectedApp?.entitlementProductKey}
                onClick={setSelectedApp}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="secondary"
          onClick={onClose}
          data-test={DATA_TEST.app_switcher_selection_dialog_cancel_button}
          data-og={DATA_OG.app_switcher_selection_dialog_cancel_button}
        >
          Cancel
        </Button>
        <Button
          data-test={DATA_TEST.app_switcher_selection_dialog_continue_button}
          data-og={DATA_OG.app_switcher_selection_dialog_continue_button}
          variant="contained"
          color="primary"
          disabled={!selectedApp}
          onClick={() => {
            const { url } = selectedApp;
            setSelectedApp(null);
            handleClose();
            navigateToEntityUrl(onEntitySelect, selectedEntity, url, (finalUrl) => window.open(finalUrl, '_self'));
          }}
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
};
